import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Globe, Menu, X } from "lucide-react";

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  language: "en" | "vi";
  alternateUrl: string;
  children: React.ReactNode;
  tableOfContents?: Array<{
    id: string;
    title: string;
    level: number;
  }>;
}

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  title,
  lastUpdated,
  language,
  alternateUrl,
  children,
  tableOfContents = [],
}) => {
  const [activeSection, setActiveSection] = React.useState<string>("");
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Check for hash in URL on initial load and scroll to it
    const hash = window.location.hash.slice(1);
    if (hash) {
      // Delay to ensure page is fully rendered
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
          setActiveSection(hash);
        }
      }, 100);
    }

    const handleScroll = () => {
      const sections = tableOfContents.map((item) =>
        document.getElementById(item.id),
      );
      const currentSection = sections.find((section) => {
        if (!section) return false;
        const rect = section.getBoundingClientRect();
        return rect.top <= 100 && rect.bottom > 100;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
        // Update URL hash while scrolling
        window.history.replaceState(null, "", `#${currentSection.id}`);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tableOfContents]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setMobileMenuOpen(false); // Close mobile menu after navigation
      // Update URL with hash without triggering page reload
      window.history.pushState(null, "", `#${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link
                to="/settings"
                className="flex items-center text-gray-600 hover:text-gray-900 transition-all duration-200 group"
              >
                <ChevronLeft className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium hidden sm:inline">
                  Back to Settings
                </span>
                <span className="font-medium sm:hidden">Back</span>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              {/* Mobile Table of Contents Button */}
              {tableOfContents.length > 0 && (
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm rounded-lg hover:bg-white hover:shadow-md transition-all duration-200 border border-gray-200/50"
                >
                  {mobileMenuOpen ? (
                    <X className="w-4 h-4 mr-2" />
                  ) : (
                    <Menu className="w-4 h-4 mr-2" />
                  )}
                  <span className="hidden sm:inline">
                    {language === "en" ? "Contents" : "Mục lục"}
                  </span>
                </button>
              )}

              <Link
                to={alternateUrl}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm rounded-lg hover:bg-white hover:shadow-md transition-all duration-200 border border-gray-200/50"
              >
                <Globe className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">
                  {language === "en" ? "Tiếng Việt" : "English"}
                </span>
                <span className="sm:hidden">
                  {language === "en" ? "VI" : "EN"}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Table of Contents */}
      {mobileMenuOpen && tableOfContents.length > 0 && (
        <div className="lg:hidden bg-white/95 backdrop-blur-sm border-b border-gray-200/50 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              {language === "en" ? "Table of Contents" : "Mục lục"}
            </h3>
            <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {tableOfContents.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                    activeSection === item.id
                      ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 font-semibold shadow-sm border border-blue-200/50"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/80 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2 flex-shrink-0"></div>
                    <span className="leading-relaxed">{item.title}</span>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Desktop */}
          {tableOfContents.length > 0 && (
            <div className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    {language === "en" ? "Table of Contents" : "Mục lục"}
                  </h3>
                  <nav className="space-y-1">
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full text-left px-4 py-3 text-sm rounded-lg transition-all duration-200 ${
                          activeSection === item.id
                            ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 font-semibold shadow-sm border border-blue-200/50"
                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50/80 hover:shadow-sm"
                        } ${item.level > 1 ? "ml-6" : ""}`}
                      >
                        <div className="flex items-center">
                          {item.level === 1 && (
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 flex-shrink-0"></div>
                          )}
                          <span className="leading-relaxed">{item.title}</span>
                        </div>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-lg overflow-hidden">
              {/* Page Header */}
              <div className="px-8 py-10 border-b border-gray-200/50 bg-gradient-to-r from-white/90 to-blue-50/30">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                      {title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-6 text-sm">
                      <div className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="font-medium">
                          {language === "en"
                            ? "Last updated:"
                            : "Cập nhật lần cuối:"}{" "}
                          <span className="text-gray-900">{lastUpdated}</span>
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        <span className="font-medium">
                          {language === "en" ? "Language:" : "Ngôn ngữ:"}{" "}
                          <span className="text-gray-900">
                            {language === "en" ? "English" : "Tiếng Việt"}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Page Content */}
              <div className="px-8 py-10">{children}</div>
            </div>

            {/* Footer Note */}
            <div className="mt-8 p-8 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 backdrop-blur-sm rounded-xl border border-blue-200/50 shadow-lg">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">
                    {language === "en" ? "Need Help?" : "Cần hỗ trợ?"}
                  </h4>
                  <p className="text-sm text-blue-800 leading-relaxed">
                    {language === "en" ? (
                      <>
                        If you have any questions about this document, please
                        contact us at{" "}
                        <a
                          href="mailto:legal@locuno.com"
                          className="font-semibold hover:underline decoration-2 underline-offset-2"
                        >
                          legal@locuno.com
                        </a>
                      </>
                    ) : (
                      <>
                        Nếu bạn có bất kỳ câu hỏi nào về tài liệu này, vui lòng
                        liên hệ với chúng tôi tại{" "}
                        <a
                          href="mailto:legal@locuno.com"
                          className="font-semibold hover:underline decoration-2 underline-offset-2"
                        >
                          legal@locuno.com
                        </a>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
